import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Status } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync, useIntervalEffect, useMountEffect } from "@react-hookz/web";
import { useRef, useState } from "react";
import * as R from "remeda";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import {
    GeneratingMultipleInsightDecksNotification,
    GeneratingSingleInsightDeckNotification,
} from "../../components/common/notification.components";
import { NotificationType, notificationStore } from "../../store/notification";
import { getGeneratedInsightsRefreshInterval } from "../../utils/generatedInsights.utils";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

const MULTIPLE_INSIGHTS_IDENTIFIER = "MULTIPLE_INSIGHTS";

export const useGetInprogressGeneratedInsights = ({
    fetchGeneratedInsights,
}: {
    fetchGeneratedInsights: () => void;
}): void => {
    const refreshIntervalRef = useRef<number | undefined>(undefined);
    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );
    const [inProgressInsightsState, inProgressInsightsAction] =
        useAsync(async () => {
            const { data } = await (
                await getCdbApiClient()
            ).getGeneratedInsights({
                advertiserId: getAdvertiserId(),
                status: Status.IN_PROGRESS,
            });
            return data ?? [];
        }, []);
    // Handle in progress Report Status
    // start with nothing in progress
    const [
        inProgressInsightTrackExecutionIds,
        setInProgressInsightTrackExecutionIds,
    ] = useState<string[]>([]);

    useAsyncErrorStatusNotification(inProgressInsightsState);

    const handleInsightsNotifications = async (
        newInProgressInsights: GeneratedInsight[],
    ) => {
        // Dismiss notifications for multiple insights
        dismissNotification(MULTIPLE_INSIGHTS_IDENTIFIER);
        const newInProgressInsightTrackExecutionIds = newInProgressInsights.map(
            (rs) => rs.identifier!,
        );
        const insightsNoLongerInProgressExecutionIds =
            inProgressInsightTrackExecutionIds.filter(
                (id) => !newInProgressInsightTrackExecutionIds.includes(id),
            );
        insightsNoLongerInProgressExecutionIds.forEach((executionId) => {
            dismissNotification(executionId);
        });
        if (newInProgressInsights.length >= 4) {
            upsertNotification(
                <GeneratingMultipleInsightDecksNotification
                    count={newInProgressInsights.length}
                />,
                NotificationType.LOADING,
                MULTIPLE_INSIGHTS_IDENTIFIER,
            );
        } else if (newInProgressInsights.length > 0) {
            newInProgressInsights.forEach((ins) => {
                upsertNotification(
                    <GeneratingSingleInsightDeckNotification
                        name={ins.template!}
                    />,
                    NotificationType.LOADING,
                    ins.identifier,
                );
            });
        }
        setInProgressInsightTrackExecutionIds(
            newInProgressInsightTrackExecutionIds,
        );
    };

    // on load
    useMountEffect(async () => {
        const inProgressInsights: GeneratedInsight[] =
            await inProgressInsightsAction.execute();
        refreshIntervalRef.current =
            getGeneratedInsightsRefreshInterval(inProgressInsights);
        await handleInsightsNotifications(inProgressInsights);
    });

    useIntervalEffect(() => {
        void (async () => {
            const newInProgressInsights: GeneratedInsight[] =
                await inProgressInsightsAction.execute();
            refreshIntervalRef.current = getGeneratedInsightsRefreshInterval(
                newInProgressInsights,
            );
            await handleInsightsNotifications(newInProgressInsights);

            // Find any new executionIds
            const newInProgressInsightExecutionIds = newInProgressInsights.map(
                (rs) => rs.identifier!,
            );
            const diffExecutionIds = R.filter(
                newInProgressInsightExecutionIds,
                R.isNot(R.isIncludedIn(inProgressInsightTrackExecutionIds)),
            );
            // If there are differences or all in progress are done, refresh insights
            if (
                !R.isEmpty(diffExecutionIds) ||
                R.isEmpty(newInProgressInsightExecutionIds)
            ) {
                fetchGeneratedInsights();
                // Empty in progress so it doesnt keep diffing
                setInProgressInsightTrackExecutionIds([]);
            }
        })();
    }, refreshIntervalRef.current);
};
